export const scaleOptions = [
  { value: 1, label: "Strongly disagree" },
  { value: 2, label: "Disagree" },
  { value: 3, label: "Somewhere in between" },
  { value: 4, label: "Agree" },
  { value: 5, label: "Strongly agree" },
];

export const diagnosticSections = [
  {
    key: "growth",
    label: "Growth & Commercial",
    questions: [
      { id: "G1", text: "We're clear about our most important business priorities." },
      { id: "G2", text: "We understand what is currently limiting our ability to grow." },
      { id: "G3", text: "We have a consistent way of generating and converting new opportunities." },
    ],
  },
  {
    key: "customer",
    label: "Customer",
    questions: [
      { id: "C1", text: "Customer enquiries are reliably captured and followed up." },
      { id: "C2", text: "Our customer onboarding and service experience is straightforward." },
      { id: "C3", text: "We systematically learn from customer feedback." },
    ],
  },
  {
    key: "operations",
    label: "Operations",
    questions: [
      { id: "O1", text: "Our important processes are clear and repeatable." },
      { id: "O2", text: "We spend relatively little time duplicating information or chasing updates." },
      { id: "O3", text: "The business can operate effectively without key people constantly intervening." },
    ],
  },
  {
    key: "people",
    label: "People & Leadership",
    questions: [
      { id: "P1", text: "Responsibilities and decision-making authority are clear." },
      { id: "P2", text: "Leaders have enough time to focus on growth and improvement rather than mostly firefighting." },
      { id: "P3", text: "The team has the skills and capacity needed for the next stage of the business." },
    ],
  },
  {
    key: "technology",
    label: "Technology, Data & AI",
    questions: [
      { id: "T1", text: "Our current technology genuinely makes work easier." },
      { id: "T2", text: "We can easily access the information we need to make good decisions." },
      { id: "T3", text: "We understand where automation or AI could create useful business value." },
    ],
  },
  {
    key: "finance",
    label: "Finance & Performance",
    questions: [
      { id: "F1", text: "We understand which parts of the business create the greatest value." },
      { id: "F2", text: "We can identify where unnecessary cost or effort is being consumed." },
      { id: "F3", text: "We measure whether investments and improvements produce the result we expected." },
    ],
  },
  {
    key: "delivery",
    label: "Delivery & Change",
    questions: [
      { id: "D1", text: "Important improvement projects have clear ownership and deadlines." },
      { id: "D2", text: "We tend to finish improvement projects rather than continually starting new ones." },
      { id: "D3", text: "New systems and ways of working are successfully adopted by the people using them." },
    ],
  },
];

export const qualitativeQuestions = [
  { id: "frustration", label: "What's the single biggest frustration in the business today?" },
  { id: "ninety_days", label: "If one part of the business worked significantly better over the next 90 days, what would you choose?" },
  { id: "opportunity", label: "What opportunity do you suspect the business might currently be missing?" },
];

export const ragConfig = {
  red: { label: "Potential constraint", chip: "bg-red-50 text-red-800 border-red-200", bar: "bg-red-400" },
  amber: { label: "Improvement opportunity", chip: "bg-amber-50 text-amber-800 border-amber-200", bar: "bg-amber-400" },
  green: { label: "Current strength", chip: "bg-emerald-50 text-emerald-800 border-emerald-200", bar: "bg-emerald-500" },
};

export const startCopy = {
  growth:
    "I'd start by getting clarity on your priorities and where growth is genuinely being constrained — before changing any processes or tools.",
  customer:
    "I'd start by mapping the customer journey from first enquiry to delivery, and fixing where enquiries, follow-ups or onboarding are losing value.",
  operations:
    "I'd start by mapping your core processes to find the duplication, bottlenecks and dependencies that are quietly consuming capacity.",
  people:
    "I'd start by clarifying responsibilities and decision rights, so leaders spend less time firefighting and the team can operate more independently.",
  technology:
    "I'd start with the tools you already have — where they're underused, disconnected or creating manual work — before recommending anything new.",
  finance:
    "I'd start by building a clearer picture of where value is created and where cost or effort is being consumed, so decisions rest on evidence.",
  delivery:
    "I'd start by giving your most important improvement project clear ownership, scope and momentum — proving that change can actually land.",
};

export function computeSnapshot(scores) {
  const dimensions = diagnosticSections.map((s) => {
    const avg = Math.round((s.questions.reduce((sum, q) => sum + scores[q.id], 0) / 3) * 10) / 10;
    const rag = avg < 2.5 ? "red" : avg > 3.7 ? "green" : "amber";
    return { key: s.key, label: s.label, average: avg, rag };
  });
  const ordered = [...dimensions].sort((a, b) => a.average - b.average);
  return {
    dimensions,
    strongest: [...dimensions].sort((a, b) => b.average - a.average)[0].key,
    focus: ordered[0].key,
    secondary: ordered[1].key,
  };
}

export function detectPatterns(snapshot) {
  const byKey = Object.fromEntries(snapshot.dimensions.map((d) => [d.key, d.average]));
  const low = (k) => byKey[k] < 2.8;
  const patterns = [];
  if (low("operations") && low("people"))
    patterns.push("Founder dependency and operational capacity — too much still relies on a small number of people.");
  if (low("growth") && low("customer"))
    patterns.push("Revenue may be leaking through the customer journey — from enquiry handling to follow-up and retention.");
  if (low("technology") && low("operations"))
    patterns.push("Manual work and disconnected tools — information is likely being copied between systems and people.");
  if (low("delivery") && !low("growth"))
    patterns.push("Good intentions, stalled delivery — improvement ideas are struggling to become finished change.");
  if (low("finance"))
    patterns.push("Limited visibility of value and cost — decisions may be made without clear evidence of what's working.");
  if (snapshot.dimensions.every((d) => d.average > 3.7))
    patterns.push("Strong foundations across the board — the opportunity likely lies in scaling and fine-tuning rather than fixing.");
  return patterns;
}
